import { isEmpty } from "lodash";
import {
  T_FaqListItemList,
  T_FaqListItems,
} from "../features/Feature_GeneralAppElements/GeneralAppElements_Types";
import { useLazySaveHomeLoanFaqDetailsQuery } from "../features/Feature_Homeloan/Homeloan_Api";
import { T_SummaryData } from "../features/Feature_Homeloan/Homeloan_Types";
import { useAppSelector } from "../redux-store/CustomReduxHooks";
import {
  useLazyFetchFaqListQuery,
  useLazyFetchFaqParentModulesQuery,
} from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import { useDispatch } from "react-redux";
import { setParentModuleList } from "../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import { setError } from "../redux-store/reducers/CsErrorSlice";
import { marked } from "marked";
import { API_STATUS_CODE } from "../utils/ApiEndpoints";
import DOMPurify from "dompurify";

interface MarkedOptions {
  gfm?: boolean;
  breaks?: boolean;
  headerIds?: boolean;
  mangle?: boolean;
}

const markedOptions: MarkedOptions = {
  gfm: true,
  breaks: true,
  headerIds: true,
  mangle: false,
};

const useHflFaqApi = () => {
  const dispatch = useDispatch();
  const [saveHomeLoanFaqDetailsApi] = useLazySaveHomeLoanFaqDetailsQuery();
  const [fetchFaqParentModules] = useLazyFetchFaqParentModulesQuery();
  const [fetchFaqListApi] = useLazyFetchFaqListQuery();

  const allSummaryData: T_SummaryData[] = useAppSelector(
    (state) => state.HomeLoanSlice.summaryData,
  );

  const callHflFaqCreateCaseApi = (questionData: T_FaqListItemList) => {
    if (!isEmpty(allSummaryData)) {
      let tempSummary = allSummaryData[0];
      let requestBody = {
        customerId: tempSummary.account_detail.rows[0].CUSTOMER_NO,
        accountNumber: tempSummary.Benificiary_Details.rows[0].LOAN_ACCOUNT_NO,
        firstName: tempSummary.account_detail.rows[0].FNAME,
        lastName: tempSummary.account_detail.rows[0].LNAME,
        mobile: tempSummary.borrower_detail.rows[0].MOBILE,
        emailId: tempSummary.borrower_detail.rows[0].E_MAIL,
        subject: "Customer FAQ",
        respText: questionData.question,
        comments: "",
        source: "ABC_Servicing_App",
        subtype: questionData.issueTypeCode || "",
        isClosed: true,
        url: "",
        category: "Query",
        lob: "HFC",
        priority: "Low",
        segment: "Finance",
        callbackDate: "",
        callbackTimeSlot: "",
        stpsRequestType: "",
        asOnDate: "",
        salaryCreditDate: "",
        dueDate: "",
        changeInEmiAmount: "",
        payableCharge: "",
        partPaymentAmount: "",
        impact: "",
        modeOfRepayment: "",
        soaFromDate: "",
        soaToDate: "",
        icFromYear: "",
        icToYear: "",
        attachmentName: "",
        attachmentType: "",
        attachmentContent: "",
      };
      saveHomeLoanFaqDetailsApi(requestBody);
    }
  };

  const callParentModuleApi = async (
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    setLoading(true);
    try {
      const response = await fetchFaqParentModules("");
      if (response.data?.code === 1) {
        setLoading(false);
        dispatch(setParentModuleList(response?.data?.payload?.data));
      } else {
        dispatch(
          setError({
            error: response.data,
            message: "FAQ Api error",
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const callFetchFaqListApi = async (
    category: string,
    module: string,
    setShowSimmer: React.Dispatch<React.SetStateAction<boolean>>,
  ): Promise<T_FaqListItems[]> => {
    setShowSimmer(true);
    try {
      const response = await fetchFaqListApi(
        {
          modules: module,
          categories: category,
        },
        false,
      );

      if (
        response?.status !== "rejected" &&
        response.data?.code === API_STATUS_CODE.SUCCESS
      ) {
        const sanitized: T_FaqListItems[] = await Promise.all(
          response.data.payload.data.map(async (data: T_FaqListItems) => {
            const markedContent = await marked(
              data.attributes.answer,
              markedOptions,
            );
            const sanitizedContent = DOMPurify.sanitize(markedContent, {
              ADD_ATTR: ["target", "rel"],
              ADD_TAGS: ["a"],
              ADD_URI_SAFE_ATTR: ["href"],
            });

            return {
              ...data,
              attributes: {
                ...data.attributes,
                answer: sanitizedContent,
              },
            };
          }),
        );

        return sanitized;
      }

      throw new Error("Fetch FAQ items failed");
    } catch (error) {
      dispatch(
        setError({
          error: new Error("Error"),
          message: "Fetch FAQ items failed",
        }),
      );
      return [];
    } finally {
      setShowSimmer(false);
    }
  };

  return { callHflFaqCreateCaseApi, callParentModuleApi, callFetchFaqListApi };
};

export default useHflFaqApi;
